import { meta, Permission } from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import { useLoadingIds } from '@hooks/useLoadingIds'
import { useMountEffect } from '@hooks/useMountEffect'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import useLoadingContext from '../../../hooks/useLoadingContext'
import TitleContext from '../../../TitleContext'
import RoleCard, { RoleCardVariants } from '../../Card/RoleCard'
import { CanAccess } from '../../Elements/Access'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import LoadingProgress from '../../Elements/LoadingProgress'
import { Page } from '../../Elements/PageMargins'
import EmptyRoles from './EmptyRoles'

const AddRole: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const title = t('Roles.Title', 'Roles')
  const { handleError } = useAppSnackbar()

  const { useTitleEffect } = useContext(TitleContext)
  useTitleEffect(title)

  const {
    Meta: { fetchPermissions: fetchPermissionsLoadingId },
  } = useLoadingIds()
  const [permissions, setPermissions] = useState<Permission[]>([])

  /**
   * Fetch permissions
   */
  const fetchPermissions = async () => {
    try {
      const fetchedPermissions = await meta.fetchPermissions()

      setPermissions(fetchedPermissions)
    } catch (err) {
      await handleError(
        err,
        t('Roles.AddRole.Error', 'Failed to fetch permissions.')
      )
    }
  }

  /** Hooks */
  const {
    triggerFetch: triggerFetchPermissions,
    isLoading: isFetchingPermissions,
  } = useLoadingContext({
    asyncFunction: fetchPermissions,
    loadingId: fetchPermissionsLoadingId,
  })

  useMountEffect(() => {
    triggerFetchPermissions()
  })

  if (isFetchingPermissions) {
    return <LoadingProgress />
  }

  const navigateToRoles = () => {
    navigate(
      {
        pathname: '/admin/roles',
      },
      {
        /** Navigation Options */
      }
    )
  }

  const breadcrumbs = [
    {
      label: t('Roles.AddRole.BreadCrumb', 'Roles'),
      onClick: navigateToRoles,
    },
    {
      label: t('Roles.AddRole.BreadCrumb', 'Add Role'),
    },
  ]

  return (
    <CanAccess I="createEditDelete" on="Role">
      <Page>
        {!permissions.length ? (
          <EmptyRoles isLoading={isFetchingPermissions} />
        ) : (
          <>
            <DynamicBreadcrumbs breadcrumbs={breadcrumbs} />
            <RoleCard
              variant={RoleCardVariants.Add}
              permissions={permissions}
            />
          </>
        )}
      </Page>
    </CanAccess>
  )
}

export default AddRole
